/* =====================================================================
   Balleh — collection (category) page.
   Right-side filter & sort drawers opened from the floating pill, facets
   that AUTO-APPLY (toggling a filter updates the grid live via the Section
   Rendering API + history — no "Apply" click), and infinite scroll.
   Loaded only on collection templates. Degrades to a native GET form +
   numbered pagination when JS is off.
   ===================================================================== */
(function () {
  'use strict';

  const root = document.querySelector('[data-collection-root]');
  if (!root) return;

  const sectionId = root.getAttribute('data-section-id');
  const scrim = document.querySelector('[data-scrim]');
  const drawers = Array.from(document.querySelectorAll('[data-collection-drawer]'));
  const filterDrawer = document.querySelector('[data-filter-drawer]');
  const sortDrawer = document.querySelector('[data-sort-drawer]');
  const filterForm = document.querySelector('[data-collection-filter-form]');
  const results = root.querySelector('[data-collection-results]');
  let lastTrigger = null;

  /* ───────── Drawers (share the global scrim + overlay pattern) ───────── */
  function openDrawer(el, trigger) {
    if (!el) return;
    lastTrigger = trigger || null;
    el.setAttribute('aria-hidden', 'false');
    el.removeAttribute('inert');
    if (scrim) scrim.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    if (window.Balleh && window.Balleh.trapFocus) {
      window.Balleh.trapFocus(el);            // move focus in + keep Tab inside the drawer
    } else {
      const close = el.querySelector('[data-drawer-close]');
      if (close) close.focus();
    }
  }

  function closeDrawers() {
    let any = false;
    for (const el of drawers) {
      if (el.getAttribute('aria-hidden') === 'false') any = true;
      el.setAttribute('aria-hidden', 'true');
      el.setAttribute('inert', '');
    }
    if (!any) return;                 // nothing of ours was open — let theme.js handle its overlays
    if (window.Balleh && window.Balleh.releaseFocus) window.Balleh.releaseFocus();
    if (scrim) scrim.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (lastTrigger) { lastTrigger.focus(); lastTrigger = null; }
  }

  root.addEventListener('click', (e) => {
    const filterOpen = e.target.closest('[data-filter-open]');
    if (filterOpen) { openDrawer(filterDrawer, filterOpen); return; }

    const sortOpen = e.target.closest('[data-sort-open]');
    if (sortOpen) { openDrawer(sortDrawer, sortOpen); return; }

    if (e.target.closest('[data-drawer-close]')) { closeDrawers(); return; }

    const opt = e.target.closest('[data-sort-value]');
    if (opt) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', opt.getAttribute('data-sort-value'));
      url.searchParams.delete('page');   // reset to first page on sort change
      window.location.href = url.toString();
    }
  });

  if (scrim) scrim.addEventListener('click', closeDrawers);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeDrawers(); });

  /* ───────── Bottom-sheet swipe-to-dismiss (mobile only) ─────────
     On phones the drawers are bottom sheets; dragging the grip (handle + header)
     downwards past a threshold closes them, mirroring a native sheet. Disabled at
     desktop widths where the drawers slide in from the side instead. */
  const sheetMq = window.matchMedia('(max-width: 1024px)');
  const CLOSE_THRESHOLD = 110;   // px dragged down before the sheet dismisses

  for (const drawer of drawers) {
    const grip = drawer.querySelector('[data-drawer-grip]');
    if (!grip) continue;
    let startY = 0;
    let delta = 0;
    let dragging = false;

    grip.addEventListener('touchstart', (e) => {
      if (!sheetMq.matches || e.touches.length !== 1) return;
      startY = e.touches[0].clientY;
      delta = 0;
      dragging = true;
      drawer.style.transition = 'none';
    }, { passive: true });

    grip.addEventListener('touchmove', (e) => {
      if (!dragging) return;
      delta = Math.max(0, e.touches[0].clientY - startY);  // only track downward drag
      drawer.style.transform = 'translateY(' + delta + 'px)';
    }, { passive: true });

    const endDrag = () => {
      if (!dragging) return;
      dragging = false;
      drawer.style.transition = '';
      drawer.style.transform = '';   // hand back to CSS (snaps closed or back open)
      if (delta > CLOSE_THRESHOLD) closeDrawers();
    };
    grip.addEventListener('touchend', endDrag);
    grip.addEventListener('touchcancel', endDrag);
  }

  /* ───────── Infinite scroll ─────────
     Re-callable: rebound after each filter swap since the grid + loader are
     replaced. The active observer is torn down before a new one is wired. */
  let io = null;

  function initInfiniteScroll() {
    if (io) { io.disconnect(); io = null; }
    const grid = root.querySelector('[data-collection-grid]');
    const loader = root.querySelector('[data-collection-loader]');
    if (!grid || !loader) return;

    const sId = grid.getAttribute('data-section-id') || sectionId;
    const totalPages = parseInt(grid.getAttribute('data-total-pages'), 10) || 1;
    let currentPage = parseInt(grid.getAttribute('data-current-page'), 10) || 1;
    let loading = false;
    let controller = null;
    const showLoader = (on) => loader.setAttribute('aria-hidden', on ? 'false' : 'true');

    async function loadMore() {
      if (loading || currentPage >= totalPages) return;
      loading = true;
      showLoader(true);

      const nextPage = currentPage + 1;
      controller?.abort();
      controller = new AbortController();

      try {
        const url = new URL(window.location.href);
        url.searchParams.set('page', String(nextPage));
        url.searchParams.set('section_id', sId);
        const res = await fetch(url.toString(), { signal: controller.signal });
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const nextGrid = doc.querySelector('[data-collection-grid]');

        if (nextGrid && nextGrid.children.length) {
          const frag = document.createDocumentFragment();
          for (const card of Array.from(nextGrid.children)) frag.appendChild(card);
          grid.appendChild(frag);
          currentPage = nextPage;
          grid.setAttribute('data-current-page', String(currentPage));
        } else {
          currentPage = totalPages;   // nothing returned — stop trying
        }
      } catch (err) {
        if (err.name !== 'AbortError') console.error(err);
      } finally {
        loading = false;
        showLoader(false);
        if (currentPage >= totalPages) { if (io) io.disconnect(); loader.remove(); }
      }
    }

    io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadMore();
    }, { rootMargin: '600px 0px' });

    if (currentPage < totalPages) io.observe(loader);
  }

  /* ───────── Filters — AJAX auto-apply (no "Apply" click) ─────────
     Any change in the facet form rebuilds the faceted URL and swaps just the
     results block via the Section Rendering API, keeping the drawer open. The
     URL is pushed so it's shareable and Back/Forward work (popstate reloads). */
  let filterController = null;

  function buildFilterUrl() {
    const params = new URLSearchParams();
    for (const [k, v] of new FormData(filterForm).entries()) {
      if (v === '' || v == null) continue;     // drop empty price fields etc.
      params.append(k, v);
    }
    const base = filterForm.getAttribute('action') || window.location.pathname;
    const qs = params.toString();
    return qs ? base + '?' + qs : base;
  }

  async function applyFilters(targetUrl) {
    if (!results) { window.location.href = targetUrl; return; }
    results.classList.add('is-loading');
    filterController?.abort();
    filterController = new AbortController();

    try {
      const fetchUrl = new URL(targetUrl, window.location.origin);
      fetchUrl.searchParams.set('section_id', sectionId);
      const res = await fetch(fetchUrl.toString(), { signal: filterController.signal });
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('[data-collection-results]');
      if (next) results.innerHTML = next.innerHTML;
      // The drawer lives outside the results block, so refresh its facets too — otherwise
      // options that now lead to 0 products stay enabled (dead-end "no products" clicks).
      // innerHTML swap keeps the <form> element (and its listeners) intact.
      const nextForm = doc.querySelector('[data-collection-filter-form]');
      if (nextForm) filterForm.innerHTML = nextForm.innerHTML;

      if (window.history && window.history.pushState) {
        window.history.pushState({ balleh: true }, '', targetUrl);
      }
      initInfiniteScroll();   // grid + loader were replaced — rebind
    } catch (err) {
      if (err.name !== 'AbortError') { console.error(err); window.location.href = targetUrl; }
    } finally {
      results.classList.remove('is-loading');
    }
  }

  function clearFilters() {
    for (const input of filterForm.querySelectorAll('input')) {
      if (input.type === 'checkbox' || input.type === 'radio') input.checked = false;
      else if (input.type === 'number' || input.type === 'text' || input.type === 'search') input.value = '';
      // hidden inputs (e.g. sort_by) are preserved
    }
    applyFilters(filterForm.getAttribute('action') || window.location.pathname);
  }

  if (filterForm && results) {
    filterForm.addEventListener('change', () => applyFilters(buildFilterUrl()));
    filterForm.addEventListener('submit', (e) => { e.preventDefault(); applyFilters(buildFilterUrl()); });

    // Delegated: the form's children are swapped after every apply (see applyFilters).
    filterForm.addEventListener('click', (e) => {
      if (e.target.closest('[data-filter-apply]')) { e.preventDefault(); closeDrawers(); }
      else if (e.target.closest('[data-filter-clear]')) { e.preventDefault(); clearFilters(); }
    });

    window.addEventListener('popstate', () => window.location.reload());
  }

  initInfiniteScroll();
})();
